// Optional onboarding step: connect a bank account (ACH) so invoices are
// paid by bank instead of card. Same Stripe flow as My Invoices → Billing
// (AddBankDialog + the client-portal billing endpoints), so a bank connected
// here is exactly what the portal shows later.
//
// Why it is worth a client's minute: card payments on invoices carry the
// ~3% card processing fee; bank payments carry none. Autopay already prefers
// a bank over a card when both are authorized (select_autopay_method).
import React, { useState } from 'react';

import { Alert, Box, Button, Link, Stack, Typography } from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import {
  createMySetupIntent,
  recordMyAutopayConsent,
  registerMyPendingBankVerification,
} from '@src/api/myBilling';
import AddBankDialog from '@src/components/billing/AddBankDialog';

export default function OnboardingBankStep({ billingProfileId, bank, onConnected }) {
  const [open, setOpen] = useState(false);

  if (!billingProfileId) return null;

  return (
    <Box>
      {bank && !bank.pending_verification && (
        <Alert severity="success" variant="outlined" icon={<CheckCircleIcon />}>
          <strong>Bank connected:</strong> {bank.bank_name} ending in {bank.last4}. Invoices are paid
          from this account, with no card processing fee.
        </Alert>
      )}
      {bank && bank.pending_verification && (
        <Alert severity="info" variant="outlined">
          <strong>{bank.bank_name} ending in {bank.last4} is waiting on verification.</strong> Stripe
          sent two small deposits to this account (1–2 business days).{' '}
          {bank.verification_url ? (
            <Link href={bank.verification_url} target="_blank" rel="noopener">
              Confirm the amounts
            </Link>
          ) : (
            'Confirm the amounts from the email Stripe sends'
          )}{' '}
          to finish — you can carry on with onboarding meanwhile.
        </Alert>
      )}
      {!bank && (
        <Stack spacing={1}>
          <Box>
            <Button
              variant="outlined"
              startIcon={<AccountBalanceIcon />}
              onClick={() => setOpen(true)}
              sx={{ textTransform: 'none' }}
            >
              Connect bank account
            </Button>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Optional. Most banks connect instantly by signing in; others verify with two small
            deposits. Nothing is charged now.
          </Typography>
        </Stack>
      )}

      <AddBankDialog
        open={open}
        onClose={() => setOpen(false)}
        profileId={billingProfileId}
        createSetupIntent={createMySetupIntent}
        registerPendingVerification={registerMyPendingBankVerification}
        onAttached={async () => {
          try {
            await recordMyAutopayConsent(billingProfileId, 'us_bank_account');
          } catch {
            /* non-fatal — staff can re-record */
          }
          onConnected?.();
        }}
        settlementCopy={
          'Invoices paid by bank take 3–5 business days to clear and carry no card processing fee. ' +
          'Your card stays on file only as the backup your agreement requires.'
        }
      />
    </Box>
  );
}
